import type {
  HttpExceptionDto,
  InlineObject16,
  UserCreateDto,
  UserDto,
  UserLoginWithOtpRequestDto,
  UserRoleDto,
} from '@/castapi';
import { router, getHomePath, EMPTY_USER_ROLE_NAME, USER_ROLE_IDS } from '@/utils';
import { AppLogger } from '@/logger';
import { getUsersApi, isKnownError, omitUserSecretFields, getErrorMessage, getSmsApi } from '@/castapi/helpers';
import { apiConfig } from '@/utils/constants';
import axios from 'axios';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { IActionParams, RootGetters, RootState } from '@/store/modules/index';
import type { RoleWithBindingDto } from '@/store/modules/organizations';
import snackbarPlugin from '@/utils/plugins/snackbar';
import { addMinutes, differenceInSeconds } from 'date-fns';

const logger = new AppLogger('login state');

const getUserIdFromAccessToken = (accessToken: string): number => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.userId;
  } catch {
    return 0;
  }
};

const buildFakeEduUser = (fields: {
  userId: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  eduRole?: string;
  role?: string;
  studentRef?: number;
  teacherRef?: number;
}): UserDto => {
  const role = fields.eduRole || fields.role;
  return {
    userId: fields.userId,
    firstName: fields.firstName || '',
    lastName: fields.lastName || '',
    email: fields.email || '',
    phoneNumber: fields.phoneNumber || '',
    active: false,
    isEducational: true,
    studentRef: role === 'student' ? (fields.studentRef ?? -1) : undefined,
    teacherRef: role === 'teacher' ? (fields.teacherRef ?? -1) : undefined,
    avatar: '',
    lastLoginDate: '',
    isMobileTwoFaAppInitialized: false,
    twoFaStartDate: '',
    emsIdentifier: '',
    recaptchaToken: '',
  } as UserDto;
};

export interface ILoggedUserEduFields {
  accessToken: string;
  userId: number;
  step: number;
  role: 'student' | 'teacher';
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  countryRef?: number;
  province?: string;
  // city?: string;
  // postalCode?: string;
  schoolName?: string;
  courseMajor?: string;
  studentId?: string;
  graduationDate?: string;
  jobTitle?: string;
  proofType1?: string;
  proofType2?: string;
  comments?: string;
  // agreeWithPolicy?: boolean;
  agreeWithTerms?: boolean;
  marketingConsent?: boolean;
  // atLeast16?: boolean;
}

interface ILoginState {
  twoFaSetupQrCodeDataUrl?: string;
  loggedUser?: UserDto;
  loggedUserEduFields: ILoggedUserEduFields | null;
  sendOtpToEmailInProgress: boolean;
  sendOtpToEmailError?: string;
  accessToken?: string;
  refreshToken?: string;
  loginError?: string;
  loggingIn: boolean;
  resetPasswordInProgress: boolean;
  resetPasswordError?: string;
  changingPassword: boolean;
  changePasswordError?: string;
  tokenValid: boolean;
  userInvitationNotFound: boolean;
  invitedUser?: UserDto;
  reloadUserDataInProgress: boolean;
  reloadUserDataError?: string;
  prevRoute?: RouteLocationNormalizedLoaded;
  phoneVerificationInProgress: boolean;
  phoneVerificationError?: string;
  isEducational: boolean;
  isTeacher: boolean;
  registrationOtpSending: boolean;
  registrationOtpSent: boolean;
  registrationOtpError?: string;
  registrationOtpVerifying: boolean;
  registrationOtpVerified: boolean;
  registrationOtpVerifyError?: string;
}

const initialState = (): ILoginState => ({
  twoFaSetupQrCodeDataUrl: undefined,
  loggedUser: undefined,
  sendOtpToEmailInProgress: false,
  sendOtpToEmailError: undefined,
  accessToken: undefined,
  refreshToken: undefined,
  loginError: undefined,
  loggingIn: false,
  resetPasswordInProgress: false,
  resetPasswordError: undefined,
  changingPassword: false,
  changePasswordError: undefined,
  tokenValid: false,
  userInvitationNotFound: false,
  invitedUser: undefined,
  reloadUserDataInProgress: false,
  reloadUserDataError: undefined,
  prevRoute: undefined,
  phoneVerificationInProgress: false,
  phoneVerificationError: undefined,
  isEducational: false,
  isTeacher: false,
  registrationOtpSending: false,
  registrationOtpSent: false,
  registrationOtpError: undefined,
  registrationOtpVerifying: false,
  registrationOtpVerified: false,
  registrationOtpVerifyError: undefined,
  loggedUserEduFields: null,
});

type Getters = {
  userId: number;
  user?: UserDto;
  accessToken: string;
  refreshToken: string;
  isAdmin: boolean;
  loggedIn: boolean;
  isEducational: boolean;
};

type ActionParams = IActionParams<ILoginState, Getters>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    SEND_OTP_TO_EMAIL_IN_PROGRESS(state: ILoginState): void {
      state.sendOtpToEmailInProgress = true;
      state.sendOtpToEmailError = undefined;
    },
    SEND_OTP_TO_EMAIL_SUCCESS(state: ILoginState): void {
      state.sendOtpToEmailInProgress = false;
      state.sendOtpToEmailError = undefined;
    },
    SEND_OTP_TO_EMAIL_ERROR(state: ILoginState, error: Error): void {
      state.sendOtpToEmailInProgress = false;
      state.sendOtpToEmailError = getErrorMessage(error);
    },
    PASSWORD_VERIFIED(state: ILoginState, data: { twoFaSetupQrCodeDataUrl: string; isEducational: boolean }): void {
      state.twoFaSetupQrCodeDataUrl = data.twoFaSetupQrCodeDataUrl;
      state.isEducational = data.isEducational || false;
      state.loggingIn = false;
    },
    OTP_SENT(state: ILoginState): void {
      // unused
      state.loggingIn = false;
    },
    ACCESS_TOKEN_RECEIVED(
      state: ILoginState,
      { accessToken, refreshToken }: { accessToken: string; refreshToken: string },
    ): void {
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
    },
    LOGIN_SUCCESS(state: ILoginState, user: UserDto): void {
      state.loggedUser = user;
      state.loggingIn = false;
      state.loginError = undefined;
    },
    LOGIN_IN_PROGRESS(state: ILoginState): void {
      state.loggingIn = true;
      state.loginError = undefined;
    },
    LOGIN_ERROR(state: ILoginState, payload: Error): void {
      state.loggingIn = false;
      state.loginError = getErrorMessage(payload);
    },
    EDU_STEP_ONE_REGISTERED(state: ILoginState): void {
      state.loggingIn = false;
    },
    CLEAR_LOGIN_ERROR(state: ILoginState): void {
      state.loginError = undefined;
    },
    RESET_PASSWORD_SUCCESS(state: ILoginState): void {
      state.resetPasswordInProgress = false;
      state.resetPasswordError = undefined;
    },
    RESET_PASSWORD_IN_PROGRESS(state: ILoginState): void {
      state.resetPasswordInProgress = true;
      state.resetPasswordError = undefined;
    },
    RESET_PASSWORD_ERROR(state: ILoginState, error: Error): void {
      state.resetPasswordInProgress = false;
      state.resetPasswordError = getErrorMessage(error);
    },
    CHANGING_PASSWORD(state: ILoginState): void {
      state.changingPassword = true;
      state.changePasswordError = undefined;
    },
    CHANGE_PASSWORD_SUCCESS(state: ILoginState): void {
      state.changingPassword = false;
      state.changePasswordError = undefined;
    },
    CHANGE_PASSWORD_ERROR(state: ILoginState, error: Error): void {
      state.changingPassword = false;
      state.changePasswordError = getErrorMessage(error);
    },
    RELOAD_USER_DATA(state: ILoginState): void {
      state.reloadUserDataInProgress = true;
      state.reloadUserDataError = undefined;
    },
    RELOAD_USER_DATA_SUCCESS(state: ILoginState, user: UserDto): void {
      state.loggedUser = user;
      state.reloadUserDataInProgress = false;
    },
    RELOAD_USER_DATA_ERROR(state: ILoginState, error: Error): void {
      state.reloadUserDataInProgress = false;
      state.reloadUserDataError = getErrorMessage(error);
    },
    RESET_STATE(state: ILoginState): void {
      const saved = state.loggedUserEduFields;
      Object.assign(state, initialState());
      state.loggedUserEduFields = saved;
    },
    SET_LOGGED_USER_EDU_FIELDS(state: ILoginState, fields: ILoggedUserEduFields): void {
      state.loggedUserEduFields = fields;
    },
    CLEAR_LOGGED_USER_EDU_FIELDS(state: ILoginState): void {
      state.loggedUserEduFields = null;
    },
    UPDATE(state: ILoginState, user: UserDto): void {
      state.loggedUser = user;
    },
    TOKEN_VALID(state: ILoginState): void {
      state.loggingIn = false;
      state.loginError = undefined;
      state.tokenValid = true;
    },
    TOKEN_NOT_VALID(state: ILoginState): void {
      state.loggingIn = false;
      state.loginError = undefined;
      state.tokenValid = false;
    },
    USER_INVITATION_DATA_LOADED(state: ILoginState, data: UserDto): void {
      state.invitedUser = data;
      state.loggingIn = false;
    },
    // CLEAR_USER_INVITATION_DATA(state: ILoginState): void {
    //   state.invitedUser = undefined;
    // },
    SET_PREV_ROUTE(state: ILoginState, route: RouteLocationNormalizedLoaded): void {
      state.prevRoute = route;
    },
    PHONE_VERIFICATION_ERROR(state: ILoginState, error: Error): void {
      state.phoneVerificationInProgress = false;
      state.phoneVerificationError = getErrorMessage(error);
    },
    PHONE_ALREADY_VERIFIED(state: ILoginState): void {
      //NEEDED FOR SUBSCRIPTION
      state.phoneVerificationInProgress = false;
    },
    PHONE_VERIFICATION_LIMIT_REACHED(state: ILoginState): void {
      //NEEDED FOR SUBSCRIPTION
      state.phoneVerificationInProgress = false;
    },
    INCORRECT_PHONE_NUMBER(state: ILoginState): void {
      //NEEDED FOR SUBSCRIPTION
      state.phoneVerificationInProgress = false;
    },
    PHONE_CODE_EXPIRED(state: ILoginState): void {
      //NEEDED FOR SUBSCRIPTION
      state.phoneVerificationInProgress = false;
    },
    VERIFYING_PHONE(state: ILoginState): void {
      state.phoneVerificationInProgress = true;
      state.phoneVerificationError = undefined;
    },
    VERIFICATION_SMS_SENT(state: ILoginState): void {
      state.phoneVerificationInProgress = false;
      state.phoneVerificationError = undefined;
    },
    PHONE_VERIFIED(state: ILoginState, phone: string): void {
      if (state.loggedUser) {
        state.loggedUser.phoneNumber = phone;
      }
      state.phoneVerificationInProgress = false;
      state.phoneVerificationError = undefined;
    },
    PHONE_NOT_VERIFIED(state: ILoginState): void {
      //NEEDED FOR SUBSCRIPTION
      state.phoneVerificationInProgress = false;
    },
    SMS_NOT_SENT(state: ILoginState): void {
      //NEEDED FOR SUBSCRIPTION
      state.phoneVerificationInProgress = false;
    },
    CLEAR_PHONE_VERIFICATION_PROGRESS(state: ILoginState): void {
      state.phoneVerificationError = undefined;
      state.phoneVerificationInProgress = false;
    },
    REGISTRATION_OTP_SENDING(state: ILoginState): void {
      state.registrationOtpSending = true;
      state.registrationOtpError = undefined;
      state.registrationOtpVerifyError = undefined;
    },
    REGISTRATION_OTP_SENT(state: ILoginState): void {
      state.registrationOtpSending = false;
      state.registrationOtpSent = true;
      state.registrationOtpError = undefined;
    },
    REGISTRATION_OTP_SEND_ERROR(state: ILoginState, error: Error): void {
      state.registrationOtpSending = false;
      state.registrationOtpError = getErrorMessage(error);
    },
    REGISTRATION_OTP_VERIFYING(state: ILoginState): void {
      state.registrationOtpVerifying = true;
      state.registrationOtpVerifyError = undefined;
    },
    REGISTRATION_OTP_VERIFIED(state: ILoginState): void {
      state.registrationOtpVerifying = false;
      state.registrationOtpVerified = true;
      state.registrationOtpVerifyError = undefined;
    },
    REGISTRATION_OTP_VERIFY_ERROR(state: ILoginState, error: Error): void {
      state.registrationOtpVerifying = false;
      state.registrationOtpVerifyError = getErrorMessage(error);
    },
    CLEAR_REGISTRATION_OTP_VERIFY_ERROR(state: ILoginState): void {
      state.registrationOtpVerifyError = undefined;
    },
    USER_REDIRECTED_AFTER_LOGIN(): void {
      // NEEDED FOR SUBSCRIPTION
    },
    PHONE_VERIFIED_FOR_EDU_REGISTRATION(): void {
      // NEEDED FOR SUBSCRIPTION
    },
  },
  actions: {
    async sendOtpToEmail({ commit }: ActionParams, email: string): Promise<void> {
      try {
        commit('SEND_OTP_TO_EMAIL_IN_PROGRESS');
        await getUsersApi().usersControllerSendOtpToEmail({ email });
        commit('SEND_OTP_TO_EMAIL_SUCCESS');
      } catch (error) {
        commit('SEND_OTP_TO_EMAIL_ERROR', error);
        logger.captureStoreError('sendOtpToEmail', error, { email });
      }
    },
    async LOGIN({ commit, dispatch }: ActionParams, user: UserLoginWithOtpRequestDto): Promise<void> {
      try {
        commit('SEND_OTP_TO_EMAIL_SUCCESS');
        commit('LOGIN_IN_PROGRESS');
        const response = await getUsersApi().usersControllerLogin(user);
        if (!response.data?.accessToken) {
          commit('PASSWORD_VERIFIED', response.data);
          return;
        }
        commit('ACCESS_TOKEN_RECEIVED', response.data);
        await dispatch('GET_CURRENT_USER_DATA');
      } catch (error) {
        commit('LOGIN_ERROR', error);
        if (isKnownError(error as HttpExceptionDto, 400, 'Invalid email or password')) {
          return;
        }
        logger.captureStoreError('LOGIN', error, { userEmail: user.email });
      }
    },
    clearLoginError({ commit }: ActionParams): void {
      commit('CLEAR_LOGIN_ERROR');
    },
    async LOGOUT({ commit, dispatch, getters }: ActionParams): Promise<void> {
      const accessToken = getters.accessToken;
      const refreshToken = getters.refreshToken;
      if (accessToken && refreshToken) {
        try {
          await getUsersApi().usersControllerLogout({ accessToken, refreshToken });
        } catch (error) {
          logger.captureStoreError('LOGOUT', error);
        }
      }
      commit('RESET_STATE');
      commit('CLEAR_LOGGED_USER_EDU_FIELDS');
      await dispatch('resetState', undefined, { root: true });
      await dispatch('adminOrganizations/resetState', undefined, { root: true });
      await dispatch('adminDiscounts/resetState', undefined, { root: true });
      await dispatch('adminDongles/resetState', undefined, { root: true });
      await dispatch('adminInventory/resetState', undefined, { root: true });
      await dispatch('adminOrders/resetState', undefined, { root: true });
      await dispatch('adminUploads/resetState', undefined, { root: true });
      await dispatch('adminReleases/resetState', undefined, { root: true });
      await dispatch('adminUsers/resetState', undefined, { root: true });
      await dispatch('adminDemos/resetState', undefined, { root: true });
      await dispatch('adminEdu/resetState', undefined, { root: true });
      await dispatch('shop/resetState', undefined, { root: true });
      await dispatch('invoices/resetState', undefined, { root: true });
      await dispatch('demo/resetState', undefined, { root: true });
      await dispatch('dongles/resetState', undefined, { root: true });
      await dispatch('organizations/resetState', undefined, { root: true });
      await dispatch('dictionary/resetState', undefined, { root: true });
      await dispatch('snackbar/resetState', undefined, { root: true });
      await dispatch('timer/resetState', undefined, { root: true });
    },
    async GET_CURRENT_USER_DATA({ commit, dispatch, getters }: ActionParams): Promise<void> {
      const accessToken = getters.accessToken;
      if (!accessToken) {
        return;
      }
      try {
        const userResponse = await getUsersApi(accessToken).usersControllerGetUser();
        const user = userResponse.data;
        if (user.isEducational && !user.active) {
          commit('LOGIN_SUCCESS', user);
          await dispatch('redirectLoggedUser');
          return;
        }
        await Promise.all([
          dispatch('organizations/getUserOrganizations', accessToken, { root: true }),
          dispatch('dictionary/getUserRoles', { accessToken }, { root: true }),
          dispatch('demo/loadDemos', accessToken, { root: true }),
        ]);
        commit('LOGIN_SUCCESS', user);
        if (getters.isEducational) {
          await dispatch('organizations/getPurchaseAvailabilityForOrganization', undefined, { root: true });
          await dispatch('organizations/getSupportAvailabilityForOrganization', undefined, { root: true });
        }
        if (!getters.isAdmin) {
          await dispatch('shop/getUserUnfinishedOrder', undefined, { root: true });
        }
        await dispatch('redirectLoggedUser');
      } catch (error) {
        commit('LOGIN_ERROR', error);
        logger.captureStoreError('GET_CURRENT_USER_DATA', error);
      }
    },
    async RELOAD_CURRENT_USER_DATA(
      { commit, dispatch, getters, rootGetters }: ActionParams,
      payload: string,
    ): Promise<void> {
      try {
        const accessToken = payload || getters.accessToken;
        const previousUser = getters.user;

        if (previousUser?.isEducational && !previousUser?.active) {
          commit('RELOAD_USER_DATA');
          const userResponse = await getUsersApi(accessToken).usersControllerGetUser();
          const freshUser = userResponse.data;
          if (freshUser.active) {
            await Promise.all([
              dispatch('organizations/getUserOrganizations', accessToken, { root: true }),
              dispatch('dictionary/getUserRoles', { accessToken }, { root: true }),
              dispatch('demo/loadDemos', accessToken, { root: true }),
            ]);
            commit('LOGIN_SUCCESS', freshUser);
            if (freshUser.isEducational) {
              await dispatch('organizations/getPurchaseAvailabilityForOrganization', undefined, { root: true });
              await dispatch('organizations/getSupportAvailabilityForOrganization', undefined, { root: true });
            }
            if (!getters.isAdmin) {
              await dispatch('shop/getUserUnfinishedOrder', undefined, { root: true });
            }
            await router.push({ path: getHomePath() });
            commit('RELOAD_USER_DATA_SUCCESS', freshUser);
            return;
          }
          commit('RELOAD_USER_DATA_SUCCESS', freshUser);
          return;
        }

        const roles = rootGetters['dictionary/userRoleTypes'];
        if (!roles.find((r: UserRoleDto) => r.userRoleId === USER_ROLE_IDS.VIRTUAL_UTILITIES_USER_ROLE_ID)) {
          await dispatch('dictionary/getUserRoles', { accessToken, force: true }, { root: true });
        }
        commit('RELOAD_USER_DATA');
        const [response] = await Promise.all([
          getUsersApi(accessToken).usersControllerGetUser(),
          dispatch('organizations/getUserOrganizations', accessToken, { root: true }),
          dispatch('demo/loadDemos', accessToken, { root: true }),
        ]);
        commit('RELOAD_USER_DATA_SUCCESS', response.data);
      } catch (error) {
        commit('RELOAD_USER_DATA_ERROR', error);
      }
    },
    async redirectLoggedUser({ getters, commit }: ActionParams): Promise<void> {
      const user = getters.user;
      if (user.isEducational && !user.active) {
        await router.push({ path: '/registration', query: { edu: 'true' } });
        return;
      }
      const redirect = router.currentRoute.value.query.redirect;
      const fullPath =
        redirect && redirect !== 'null' && redirect !== 'undefined' ? (redirect as string) : getHomePath();
      await router.push({ path: fullPath });
      commit('USER_REDIRECTED_AFTER_LOGIN');
    },
    async REGISTER_USER({ commit, dispatch, getters }: ActionParams, user: UserCreateDto): Promise<void> {
      try {
        commit('LOGIN_IN_PROGRESS');
        const accessToken = getters.accessToken;
        const requestOptions = accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {};
        const response = await getUsersApi(accessToken).usersControllerRegister(user, requestOptions);
        commit('ACCESS_TOKEN_RECEIVED', response.data);
        if (router.currentRoute.value.path === '/registration' && router.currentRoute.value.query.edu === 'true') {
          const newAccessToken = getters.accessToken;
          const userId = getUserIdFromAccessToken(newAccessToken);
          commit('LOGIN_SUCCESS', buildFakeEduUser({ userId, ...user }));
        } else {
          await dispatch('GET_CURRENT_USER_DATA');
        }
      } catch (error) {
        commit('LOGIN_ERROR', error);
        if (isKnownError(error as HttpExceptionDto, 409, /Organization with specified email already exists/)) {
          return;
        }
        if (isKnownError(error as HttpExceptionDto, 401, /Invalid credentials/)) {
          return;
        }
        logger.captureStoreError('REGISTER_USER', error, omitUserSecretFields(user));
      }
    },
    async SAVE_EDU_DETAILS(
      { commit, getters }: ActionParams,
      payload: Record<string, unknown>,
    ): Promise<string | undefined> {
      try {
        commit('LOGIN_IN_PROGRESS');
        const accessToken = getters.accessToken;
        await axios.post(`${apiConfig.basePath}/api/users/save-edu-details`, payload, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        commit('EDU_STEP_ONE_REGISTERED');
        return undefined;
      } catch (error) {
        const axiosError = error as { response?: { data?: { message?: string }; status?: number } };
        const backendMessage = axiosError?.response?.data?.message;
        if (axiosError?.response?.status === 400 && backendMessage) {
          commit('EDU_STEP_ONE_REGISTERED');
          return backendMessage;
        }
        commit('LOGIN_ERROR', error);
        logger.captureStoreError('SAVE_EDU_DETAILS', error);
        return undefined;
      }
    },
    async SAVE_EDU_PROOF({ commit, getters }: ActionParams, payload: Record<string, unknown>): Promise<void> {
      try {
        commit('LOGIN_IN_PROGRESS');
        const accessToken = getters.accessToken;
        await axios.post(`${apiConfig.basePath}/api/users/save-edu-proof`, payload, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        commit('EDU_STEP_ONE_REGISTERED');
      } catch (error) {
        commit('LOGIN_ERROR', error);
        logger.captureStoreError('SAVE_EDU_PROOF', error);
      }
    },
    async SEND_REGISTRATION_OTP({ commit, getters }: ActionParams): Promise<void> {
      try {
        commit('REGISTRATION_OTP_SENDING');
        const accessToken = getters.accessToken;
        await axios.post(
          `${apiConfig.basePath}/api/users/send-registration-otp`,
          {},
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        commit('REGISTRATION_OTP_SENT');
      } catch (error) {
        commit('REGISTRATION_OTP_SEND_ERROR', error);
        logger.captureStoreError('SEND_REGISTRATION_OTP', error);
      }
    },
    async VERIFY_REGISTRATION_EMAIL_OTP({ commit, getters }: ActionParams, otp: string): Promise<void> {
      try {
        commit('REGISTRATION_OTP_VERIFYING');
        const accessToken = getters.accessToken;
        await axios.post(
          `${apiConfig.basePath}/api/users/verify-registration-email-otp`,
          { otp },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        commit('REGISTRATION_OTP_VERIFIED');
      } catch (error) {
        commit('REGISTRATION_OTP_VERIFY_ERROR', error);
        logger.captureStoreError('VERIFY_REGISTRATION_EMAIL_OTP', error);
      }
    },
    async updateUser({ commit }: ActionParams, user: UserDto): Promise<void> {
      commit('UPDATE', user);
      if (!user.phoneValidated) {
        await router.push({ name: 'phoneValidation', query: { redirect: router.currentRoute.value.fullPath } });
      }
    },
    async resetPassword({ commit }: ActionParams, email: string): Promise<void> {
      try {
        commit('RESET_PASSWORD_IN_PROGRESS');
        await getUsersApi().usersControllerSendResetPasswordEmail(email);
        commit('RESET_PASSWORD_SUCCESS');
      } catch (error) {
        commit('RESET_PASSWORD_ERROR', error);
        logger.captureStoreError('resetPassword', error, { email });
      }
    },
    async checkPasswordResetToken({ commit }: ActionParams, token: string): Promise<void> {
      try {
        commit('LOGIN_IN_PROGRESS');
        const response = await getUsersApi().usersControllerCheckPasswordResetToken(token);
        if (response.data) {
          commit('TOKEN_VALID');
        } else {
          commit('TOKEN_NOT_VALID');
        }
      } catch (error) {
        commit('LOGIN_ERROR', error);
        logger.captureStoreError('checkPasswordResetToken', error);
      }
    },
    async setNewPassword(
      { commit }: ActionParams,
      { token, password }: { token: string; password: string },
    ): Promise<void> {
      commit('CHANGING_PASSWORD');
      try {
        await getUsersApi().usersControllerSetNewPassword({ token, password });
        commit('CHANGE_PASSWORD_SUCCESS');
        await router.push({ name: 'loginSignin', query: { passwordChanged: 'true' } });
      } catch (error) {
        commit('CHANGE_PASSWORD_ERROR', error);
        logger.captureStoreError('setNewPassword', error);
      }
    },
    async getUserByInvitation({ commit }: ActionParams, token: string): Promise<void> {
      commit('LOGIN_IN_PROGRESS');
      try {
        const response = await getUsersApi().usersControllerGetUserByInvitation({ token } as InlineObject16);
        commit('USER_INVITATION_DATA_LOADED', response.data);
      } catch (error) {
        await router.push({ path: getHomePath() });
        snackbarPlugin.error(getErrorMessage(error as Error), 20);
        commit('LOGIN_ERROR', error);
        logger.captureStoreError('getUserByInvitation', error);
      }
    },
    async join({ commit, dispatch, getters }: ActionParams, token: string): Promise<void> {
      commit('LOGIN_IN_PROGRESS');
      try {
        if (!getters.loggedIn || !getters.user?.active) {
          throw new Error('User data is required to use invitation');
        }
        await getUsersApi(getters.accessToken).usersControllerJoin({ token });
        // commit('CLEAR_USER_INVITATION_DATA');
        await dispatch('GET_CURRENT_USER_DATA');
        snackbarPlugin.success('You have successfully joined the organization', 20);
      } catch (error) {
        commit('LOGIN_ERROR', error);
        logger.captureStoreError('join', error);
      }
    },
    async completeRegistrationAndJoin(
      { commit, dispatch }: ActionParams,
      { token, user }: { token: string; user?: UserDto },
    ): Promise<void> {
      commit('LOGIN_IN_PROGRESS');
      try {
        if (!user) {
          throw new Error('User data is required to use invitation');
        }
        const response = await getUsersApi().usersControllerCompleteRegistrationAndJoin({ token, user });
        commit('ACCESS_TOKEN_RECEIVED', response.data);
        // commit('CLEAR_USER_INVITATION_DATA');
        await dispatch('GET_CURRENT_USER_DATA');
      } catch (error) {
        commit('LOGIN_ERROR', error);
        logger.captureStoreError('join', error);
      }
    },
    async refreshToken({ commit, getters }: ActionParams): Promise<void> {
      try {
        const refreshToken = getters.refreshToken;
        if (!refreshToken) {
          return;
        }
        const response = await getUsersApi().usersControllerRefreshToken({ refreshToken });
        commit('ACCESS_TOKEN_RECEIVED', response.data);
      } catch (error) {
        logger.captureStoreError('refreshToken', error);
      }
    },
    async changePassword(
      { commit, getters }: ActionParams,
      { password, oldPassword }: { password: string; oldPassword: string },
    ): Promise<void> {
      const accessToken = getters.accessToken;
      if (!accessToken) {
        return;
      }
      commit('CHANGING_PASSWORD');
      try {
        const response = await getUsersApi(accessToken).usersControllerChangeUserPassword({
          newPassword: password,
          oldPassword,
        });
        commit('ACCESS_TOKEN_RECEIVED', response.data);
        commit('CHANGE_PASSWORD_SUCCESS');
      } catch (error) {
        commit('CHANGE_PASSWORD_ERROR', error);
        logger.captureStoreError('changePassword', error);
      }
    },
    async sendVerificationSms(
      { commit, dispatch, getters }: ActionParams,
      { phone }: { phone: string },
    ): Promise<void> {
      const accessToken = getters.accessToken;
      if (!accessToken) {
        return;
      }
      commit('VERIFYING_PHONE');
      try {
        await getSmsApi(accessToken).smsControllerSendVerificationCodeToPhone({
          phone,
        });
        dispatch('timer/start', 120, { root: true });
        commit('VERIFICATION_SMS_SENT');
      } catch (error) {
        if (getErrorMessage(error) === 'You have reached the limit of phone number changes for today') {
          commit('PHONE_VERIFICATION_LIMIT_REACHED');
          return;
        }
        if (getErrorMessage(error) === 'Invalid phone number') {
          commit('INCORRECT_PHONE_NUMBER');
          return;
        }
        if (error.data.name === 'PHONE_ALREADY_VERIFIED') {
          commit('PHONE_ALREADY_VERIFIED', error.data.ownerEmail);
          return;
        }
        if (error.data.name === 'TOO_MANY_VERIFICATION_ATTEMPTS') {
          const timeLeft = differenceInSeconds(
            addMinutes(new Date(error.data.lastPhoneValidationAttempt), 2),
            new Date(),
          );
          dispatch('timer/start', timeLeft, { root: true });
          commit('SMS_NOT_SENT');
          return;
        }
        commit('PHONE_VERIFICATION_ERROR', error);
        logger.captureStoreError('sendVerificationSms', error);
      }
    },
    async verifyPhone({ commit, dispatch, getters }: ActionParams, { code }: { code: string }): Promise<void> {
      const accessToken = getters.accessToken;
      if (!accessToken) {
        return;
      }
      commit('VERIFYING_PHONE');
      try {
        const response = await getSmsApi(accessToken).smsControllerVerifyPhone({
          code,
        });
        if (response.data) {
          commit('PHONE_VERIFIED');
          await dispatch('RELOAD_CURRENT_USER_DATA', accessToken);
          await dispatch('redirectLoggedUser');
          snackbarPlugin.success('Thank you for validating your phone number.', 20);
        } else {
          commit('PHONE_NOT_VERIFIED');
        }
      } catch (error) {
        if (error.data.name === 'PHONE_ALREADY_VERIFIED') {
          commit('PHONE_ALREADY_VERIFIED', error.data.ownerEmail);
          return;
        }
        if (getErrorMessage(error) === 'Verification code expired or invalid') {
          commit('PHONE_CODE_EXPIRED');
          return;
        }
        commit('PHONE_VERIFICATION_ERROR', error);
        logger.captureStoreError('verifyPhone', error);
      }
    },
    async verifyPhoneForRegistration({ commit, getters }: ActionParams, { code }: { code: string }): Promise<void> {
      const accessToken = getters.accessToken;
      if (!accessToken) {
        return;
      }
      commit('VERIFYING_PHONE');
      try {
        const response = await getSmsApi(accessToken).smsControllerVerifyPhone({ code });
        if (response.data) {
          commit('PHONE_VERIFIED');
          //await dispatch('GET_CURRENT_USER_DATA');
          commit('PHONE_VERIFIED_FOR_EDU_REGISTRATION');
        } else {
          commit('PHONE_NOT_VERIFIED');
        }
      } catch (error) {
        if (error.data.name === 'PHONE_ALREADY_VERIFIED') {
          commit('PHONE_ALREADY_VERIFIED', error.data.ownerEmail);
          return;
        }
        if (getErrorMessage(error) === 'Verification code expired or invalid') {
          commit('PHONE_CODE_EXPIRED');
          return;
        }
        commit('PHONE_VERIFICATION_ERROR', error);
        logger.captureStoreError('verifyPhoneForRegistration', error);
      }
    },

    async hideProAd({ getters }: ActionParams): Promise<void> {
      const accessToken = getters.accessToken;
      if (!accessToken) {
        return;
      }
      try {
        await getUsersApi(accessToken).usersControllerHideProAd();
      } catch (error) {
        logger.captureStoreError('hideProAd', error);
      }
    },

    setPrevRoute({ commit }: ActionParams, route: RouteLocationNormalizedLoaded): void {
      commit('SET_PREV_ROUTE', route);
    },
  },
  getters: {
    twoFaSetupQrCodeDataUrl: (state: ILoginState): undefined | string => state.twoFaSetupQrCodeDataUrl,
    loggedIn: (_state: ILoginState, getters: Getters): boolean => Boolean(getters.userId),
    isAdmin: (_state: ILoginState, getters: Getters, _rootState: RootState, rootGetters: RootGetters): boolean =>
      getters.userId && rootGetters['organizations/isAdmin'](getters.userId),
    // roleId: (state: ILoginState, getters:Getters, rootState:Dummy, rootGetters:Dummy) =>
    //   getters.userId ? rootGetters['organizations/roleId'](getters.userId) || USER_ROLE_IDS.NOBODY_USER_ROLE_ID : undefined,
    roleName: (
      _state: ILoginState,
      getters: Getters,
      _rootState: RootState,
      rootGetters: RootGetters,
    ): undefined | string =>
      getters.userId ? rootGetters['organizations/roleName'](getters.userId) || EMPTY_USER_ROLE_NAME : undefined,
    isOwner: (state: ILoginState, _getters: Getters, _rootState: RootState, rootGetters: RootGetters): boolean =>
      rootGetters['organizations/isOwner'](state.loggedUser?.userId),
    isDemo: (state: ILoginState, _getters: Getters, _rootState: RootState, rootGetters: RootGetters): boolean =>
      rootGetters['organizations/isDemo'](state.loggedUser?.userId),
    isMember: (state: ILoginState, _getters: Getters, _rootState: RootState, rootGetters: RootGetters): boolean =>
      rootGetters['organizations/isMember'](state.loggedUser?.userId),
    isEmptyRole: (state: ILoginState, _getters: Getters, _rootState: RootState, rootGetters: RootGetters): boolean =>
      rootGetters['organizations/isEmptyRole'](state.loggedUser?.userId),
    activeUserMainRoleWithPermissions: (
      _state: ILoginState,
      getters: Getters,
      _rootState: RootState,
      rootGetters: RootGetters,
    ): undefined | UserRoleDto =>
      getters.userId ? rootGetters['organizations/activeUserMainRoleWithPermissions'](getters.userId) : undefined,
    user: (state: ILoginState): undefined | UserDto => state.loggedUser,
    userId: (state: ILoginState): undefined | number => state.loggedUser?.userId,
    userPhone: (state: ILoginState): undefined | string => state.loggedUser?.phoneNumber,
    sendOtpToEmailInProgress: (state: ILoginState): boolean => state.sendOtpToEmailInProgress,
    sendOtpToEmailError: (state: ILoginState): undefined | string => state.sendOtpToEmailError,
    accessToken: (state: ILoginState): undefined | string => state.accessToken,
    refreshToken: (state: ILoginState): undefined | string => state.refreshToken,
    loggingIn: (state: ILoginState): boolean => state.loggingIn,
    loginError: (state: ILoginState): undefined | string => state.loginError,
    resetPasswordInProgress: (state: ILoginState): boolean => state.resetPasswordInProgress,
    resetPasswordError: (state: ILoginState): undefined | string => state.resetPasswordError,
    changingPassword: (state: ILoginState): boolean => state.changingPassword,
    changePasswordError: (state: ILoginState): undefined | string => state.changePasswordError,
    invitedUser: (state: ILoginState): undefined | UserDto => state.invitedUser,
    userRoles: (
      _state: ILoginState,
      getters: Getters,
      _rootState: RootState,
      rootGetters: RootGetters,
    ): RoleWithBindingDto => rootGetters['organizations/activeUserRoles'](getters.userId),
    prevRoute: (state: ILoginState): undefined | RouteLocationNormalizedLoaded => state.prevRoute,
    phoneValidated: (state: ILoginState): boolean => Boolean(state.loggedUser?.phoneValidated),
    phoneVerificationInProgress: (state: ILoginState): boolean => state.phoneVerificationInProgress,
    phoneVerificationError: (state: ILoginState): undefined | string => state.phoneVerificationError,
    isEducational: (state: ILoginState): boolean => Boolean(state.loggedUser?.isEducational),
    isTeacher: (state: ILoginState): boolean => Boolean(state.loggedUser?.teacherRef),
    registrationOtpSending: (state: ILoginState): boolean => state.registrationOtpSending,
    registrationOtpSent: (state: ILoginState): boolean => state.registrationOtpSent,
    registrationOtpError: (state: ILoginState): undefined | string => state.registrationOtpError,
    registrationOtpVerifying: (state: ILoginState): boolean => state.registrationOtpVerifying,
    registrationOtpVerified: (state: ILoginState): boolean => state.registrationOtpVerified,
    registrationOtpVerifyError: (state: ILoginState): undefined | string => state.registrationOtpVerifyError,
    loggedUserEduFields: (state: ILoginState): ILoggedUserEduFields | null => state.loggedUserEduFields,
    showProAd: (state: ILoginState): boolean =>
      Boolean(state.loggedUser?.isEducational && !state.loggedUser?.hideProAd),
    userActive: (state: ILoginState): boolean => Boolean(state.loggedUser?.active),
  },
};
